import React, { useState, useEffect } from 'react';
import { getPdfViewerUrl } from '../lib/pdfDetector';
import { playClick, playSuccess } from '../lib/soundEffects';

export default function PdfViewerModal({
  isOpen,
  onClose,
  bookmark,
  onUpdateBookmark,
}) {
  const [notes, setNotes] = useState('');
  const [savedStatus, setSavedStatus] = useState(false);
  const [zoomLevel, setZoomLevel] = useState('FitH');

  useEffect(() => {
    if (bookmark) {
      setNotes(bookmark.notes || '');
      setSavedStatus(false);
    }
  }, [bookmark]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        playClick();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !bookmark) return null;

  const handleSaveNotes = () => {
    playClick();
    if (onUpdateBookmark) {
      onUpdateBookmark({
        ...bookmark,
        notes: notes.trim(),
      });
      setSavedStatus(true);
      playSuccess();
      setTimeout(() => setSavedStatus(false), 2500);
    }
  };

  const handleCopyNotes = () => {
    playClick();
    navigator.clipboard.writeText(notes);
    playSuccess();
  };

  const viewerUrl = getPdfViewerUrl(bookmark.url);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.88)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '95vw',
          maxWidth: '1280px',
          height: '90vh',
          backgroundColor: '#070d14',
          border: '1px solid rgba(16, 185, 129, 0.35)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.95), 0 0 35px rgba(16, 185, 129, 0.15)',
          overflow: 'hidden',
          color: '#e2e8f0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Control Bar */}
        <div
          style={{
            padding: '0.75rem 1.25rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#050a10',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden' }}>
            <span style={{ fontSize: '1.25rem' }}>📄</span>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#10b981' }}>
                {bookmark.title || 'PDF Document Reader'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {bookmark.url}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <a
              href={bookmark.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.4rem 0.8rem',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#94a3b8',
                fontSize: '0.75rem',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
            >
              ↗️ External Tab
            </a>
            <button
              onClick={() => { playClick(); onClose(); }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                fontSize: '1.25rem',
                cursor: 'pointer',
                padding: '0.3rem 0.5rem',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Main Content Area: PDF Viewport + Annotation Sidecar */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* PDF Viewer Frame */}
          <div style={{ flex: 1, backgroundColor: '#111827', position: 'relative' }}>
            <iframe
              src={viewerUrl}
              title={bookmark.title}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>

          {/* Research Annotation Sidecar */}
          <div
            style={{
              width: '360px',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: '#070e17',
              display: 'flex',
              flexDirection: 'column',
              padding: '1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#38bdf8' }}>
                📝 RESEARCH ANNOTATIONS
              </div>
              <button
                onClick={handleCopyNotes}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                }}
              >
                📋 Copy
              </button>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Record takeaways, quotes, formulas, or key findings while reading..."
              style={{
                flex: 1,
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: '#e2e8f0',
                fontFamily: 'system-ui, sans-serif',
                fontSize: '0.85rem',
                lineHeight: '1.5',
                resize: 'none',
                boxSizing: 'border-box',
              }}
            />

            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.75rem', color: savedStatus ? '#10b981' : '#64748b' }}>
                {savedStatus ? '✓ Saved to Bookmark' : 'Auto-syncs with notes'}
              </span>
              <button
                onClick={handleSaveNotes}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
